import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang, t, tx } from '../context/LanguageContext';
import translations from '../i18n/translations';
import solutions from '../data/solutions';
import SEO from '../components/SEO';
import { SEO_TEXTES, SEO_TEXTES_HORS_PRERENDU } from '../data/seoTextes';
import { Shield, Phone, Printer, Server, Brain, GraduationCap, ArrowRight, ExternalLink, CheckCircle } from 'lucide-react';

const iconMap = { Shield, Phone, Printer, Server, Brain, GraduationCap };

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function Solution() {
  const { slug } = useParams();
  const { lang } = useLang();
  const sol = solutions[slug];

  if (!sol) {
    return (
      <>
        <SEO {...SEO_TEXTES_HORS_PRERENDU['solution-introuvable']} path={`/solutions/${slug}`} noindex />
      <main className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-slate-400 mb-8">{tx(lang, 'Solution introuvable', 'Solution not found', { es: 'Solución no encontrada', de: 'Lösung nicht gefunden', it: 'Soluzione non trovata', pt: 'Solução não encontrada' })}</p>
          <Link to="/" className="text-[#0099D6] hover:underline">{t(translations.common.back, lang)}</Link>
        </div>
      </main>
      </>
    );
  }

  const Icon = iconMap[sol.icon];
  // Textes de src/data/seoTextes.js (une entrée par solution de src/data/solutions.js : test dans seoTextes.test.js).
  const seoMeta = SEO_TEXTES[`/solutions/${slug}`] || {};

  return (
    <>
      <SEO {...seoMeta} path={`/solutions/${slug}`} />
    <main data-testid={`solution-page-${slug}`}>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${sol.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0B0C10]/95 to-[#0B0C10]" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#0099D6] mb-8 transition-colors" data-testid="back-to-home">
              <ArrowRight className="w-4 h-4 rotate-180" />
              {t(translations.common.back, lang)}
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#0099D6]/10 flex items-center justify-center">
                {Icon && <Icon className="w-7 h-7 text-[#0099D6]" />}
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
              {t(translations.pillars.find(p => p.id === slug)?.title, lang)}
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-3xl">
              {t(sol.promise, lang)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {tx(lang, 'Bénéfices', 'Benefits', { es: 'Beneficios', de: 'Vorteile', it: 'Vantaggi', pt: 'Benefícios' })}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {tx(lang, 'Ce que ça change pour vous', 'What it changes for you', { es: 'Lo que cambia para usted', de: 'Was sich für Sie ändert', it: 'Cosa cambia per voi', pt: 'O que muda para si' })}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sol.benefits.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="flex items-start gap-4 bg-[#15171E]/60 border border-white/5 rounded-xl p-5"
              >
                <CheckCircle className="w-5 h-5 text-[#0099D6] mt-0.5 flex-shrink-0" />
                <p className="text-slate-300 text-sm leading-relaxed">{t(b, lang)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 md:py-32 bg-[#15171E]/50" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-3">
              {tx(lang, 'Cas concrets', 'Real cases', { es: 'Casos concretos', de: 'Konkrete Fälle', it: 'Casi concreti', pt: 'Casos concretos' })}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
              {tx(lang, 'Situation. Action. Resultat.', 'Situation. Action. Result.', { es: 'Situación. Acción. Resultado.', de: 'Situation. Aktion. Ergebnis.', it: 'Situazione. Azione. Risultato.', pt: 'Situação. Ação. Resultado.' })}
            </h2>
          </motion.div>
          <div className="space-y-6">
            {sol.useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-2">
                      {tx(lang, 'Situation', 'Situation', { es: 'Situación', de: 'Situation', it: 'Situazione', pt: 'Situação' })}
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">{t(uc.situation, lang)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-2">
                      Action
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">{t(uc.action, lang)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-2">
                      {tx(lang, 'Résultat', 'Result', { es: 'Resultado', de: 'Ergebnis', it: 'Risultato', pt: 'Resultado' })}
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">{t(uc.result, lang)}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof Partners */}
      <section className="py-16" data-testid="proof-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-500 mr-2">{tx(lang, 'Partenaires & certifications :', 'Partners & certifications:', { es: 'Socios y certificaciones:', de: 'Partner & Zertifizierungen:', it: 'Partner e certificazioni:', pt: 'Parceiros e certificações:' })}</span>
            {sol.proofPartners.map((p, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full bg-[#0099D6]/10 text-xs font-medium text-[#0099D6] border border-[#0099D6]/20">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + External Link */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-transparent to-[#0099D6]/5" data-testid="solution-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-8">
              {t(sol.cta, lang)}
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/prendre-rdv"
                data-testid="solution-main-cta"
                className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_30px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95"
              >
                {t(translations.common.rdv_cta, lang)}
                <ArrowRight className="w-5 h-5" />
              </Link>
              {sol.externalLink && (
                <a
                  href={sol.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="solution-external-link"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-4 px-8 rounded-lg transition-all text-sm"
                >
                  {t(sol.externalLabel, lang)}
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
    </>
  );
}
